import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert
} from '@mui/material';
import {
  Business as TenantIcon,
  CheckCircle as ActiveIcon,
  PauseCircle as SuspendedIcon,
  HourglassEmpty as PendingIcon,
  Warning as ExpiredIcon,
  Favorite as HealthyIcon,
  HeartBroken as UnhealthyIcon,
  Add as AddIcon,
  AccessTime as RecentIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { BRAND } from '../../constants/branding';

const StatCard = ({ title, value, icon, color, onClick }) => (
  <Card
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s',
      '&:hover': onClick ? { transform: 'translateY(-4px)' } : {}
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, color }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 2,
            bgcolor: `${color}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 30, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/tenants/stats');
      setStats(response.data.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all {BRAND.productName} tenant instances
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/tenants/new')}
          sx={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)'
          }}
        >
          Add Tenant
        </Button>
      </Box>

      {/* Main Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Tenants"
            value={stats?.total || 0}
            icon={<TenantIcon />}
            color="#6366f1"
            onClick={() => navigate('/tenants')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active"
            value={stats?.active || 0}
            icon={<ActiveIcon />}
            color="#10b981"
            onClick={() => navigate('/tenants?status=active')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Suspended"
            value={stats?.suspended || 0}
            icon={<SuspendedIcon />}
            color="#f59e0b"
            onClick={() => navigate('/tenants?status=suspended')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending"
            value={stats?.pending || 0}
            icon={<PendingIcon />}
            color="#8b5cf6"
            onClick={() => navigate('/tenants?status=pending')}
          />
        </Grid>
      </Grid>

      {/* Health Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Health Status
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: '#10b98120', borderRadius: 2 }}>
                  <HealthyIcon sx={{ fontSize: 40, color: '#10b981' }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>
                    {stats?.health?.healthy || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Healthy
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: '#ef444420', borderRadius: 2 }}>
                  <UnhealthyIcon sx={{ fontSize: 40, color: '#ef4444' }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#ef4444' }}>
                    {stats?.health?.unhealthy || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unhealthy
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: '#94a3b820', borderRadius: 2 }}>
                  <TenantIcon sx={{ fontSize: 40, color: '#94a3b8' }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#94a3b8' }}>
                    {stats?.health?.unknown || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unknown
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Plans Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Tenants by Plan
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: '#64748b20', borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#64748b' }}>
                    {stats?.byPlan?.basic || 0}
                  </Typography>
                  <Chip label="Basic" size="small" sx={{ mt: 1 }} />
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: '#3b82f620', borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                    {stats?.byPlan?.standard || 0}
                  </Typography>
                  <Chip label="Standard" size="small" color="primary" sx={{ mt: 1 }} />
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: '#a855f720', borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#a855f7' }}>
                    {stats?.byPlan?.enterprise || 0}
                  </Typography>
                  <Chip label="Enterprise" size="small" color="secondary" sx={{ mt: 1 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Tenants */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <RecentIcon color="primary" />
            <Typography variant="h6">Recent Tenants</Typography>
          </Box>
          <List>
            {stats?.recentTenants?.map((tenant, idx) => (
              <React.Fragment key={tenant.id}>
                {idx > 0 && <Divider />}
                <ListItem
                  button
                  onClick={() => navigate(`/tenants/${tenant.id}`)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemIcon>
                    <TenantIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={tenant.tenant_name}
                    secondary={
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {tenant.tenant_id}
                      </Typography>
                    }
                  />
                  <Chip
                    label={tenant.status}
                    size="small"
                    color={
                      tenant.status === 'active' ? 'success' :
                      tenant.status === 'suspended' ? 'warning' :
                      tenant.status === 'pending' ? 'info' : 'default'
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
            {(!stats?.recentTenants || stats.recentTenants.length === 0) && (
              <ListItem>
                <ListItemText
                  primary="No tenants yet"
                  secondary="Create your first tenant to get started"
                />
              </ListItem>
            )}
          </List>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button onClick={() => navigate('/tenants')}>
              View All Tenants
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
